// PDF export of My Plants.
//
// Dynamic vertical flow: cards are packed onto the page and a page break is
// only inserted when the next card can't fit — no more one-plant-per-page
// whitespace. Every card now includes the full identification: badges,
// state-status note, keystone species, companion plant + reason, common
// pests with symptoms & natural remedies, medicinal / traditional uses,
// cultural uses, and notes.
import { jsPDF } from "jspdf";
import { idNative, idEndangered, idStateNote } from "@/lib/idCompat";
import { localizedIdentification } from "@/lib/localizedId";

const yesNo = (v, t) => (v === true ? t("pdf.yes") : v === false ? t("pdf.no") : t("pdf.unknown"));

// i18next has interpolation.escapeValue = true, so t("pdf.scanned", {date: ...})
// returns strings like "Scanned: 7&#x2F;5&#x2F;2026". React would decode
// these on render, but jsPDF renders literal text. Decode a handful of the
// most common numeric HTML entities before drawing to keep PDFs clean.
const decodeHtmlEntities = (s) => {
  if (typeof s !== "string") return s;
  return s
    .replace(/&#x2F;/g, "/")
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
};

const drawImage = (doc, dataUrl, x, y, w, h) => {
  try {
    const type = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
    doc.addImage(dataUrl, type, x, y, w, h, undefined, "FAST");
    return true;
  } catch {
    doc.setDrawColor(180);
    doc.rect(x, y, w, h);
    return false;
  }
};

// Pretty-print sun_preference tokens ("full_sun" → "Full sun").
const prettySunToken = (s, t) => {
  const map = {
    full_sun: t("result.sunFull"),
    partial_sun: t("result.sunPartial"),
    shade: t("result.sunShade"),
  };
  return map[s] || s;
};

export async function exportPlantsPdf(items, { language, state, userName, t, shareUrl, output = "save", filename = "prairie_plants.pdf", includeToc = false }) {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  // Reserve space at the bottom for a running footer that pagination logic
  // never overwrites.
  const bottomLimit = pageH - margin;

  const drawHeaderBar = (pageNumber, totalPages) => {
    doc.setFillColor(45, 74, 34);
    doc.rect(0, 0, pageW, 24, "F");
    doc.setTextColor(255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(decodeHtmlEntities(`Prairie · ${state}`), margin, 16);
    if (totalPages != null) {
      doc.text(`${pageNumber} / ${totalPages}`, pageW - margin, 16, { align: "right" });
    }
  };

  // ------------------ Cover page ------------------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(30);
  doc.text("Prairie", margin, margin + 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.setTextColor(110);
  doc.text(decodeHtmlEntities(t("pdf.subtitle", { state })), margin, margin + 48);
  doc.setTextColor(40);
  doc.setFontSize(11);
  doc.text(
    decodeHtmlEntities(t("pdf.exportedBy", { name: userName || "—", count: items.length, date: new Date().toLocaleString() })),
    margin, margin + 78
  );
  if (shareUrl) {
    doc.setFontSize(10); doc.setTextColor(90);
    doc.text(
      decodeHtmlEntities(t("pdf.shareUrl", { url: shareUrl })),
      margin, margin + 98,
      { maxWidth: pageW - 2 * margin }
    );
    doc.setTextColor(40);
  }

  // ------------------ Plant cards (dynamic flow) ------------------
  // Add the first plant-page canvas. Don't draw a header bar here — the
  // loop's group-divider logic will overlay this page with the first
  // section cover (parchment fill), and drawing the header bar first would
  // leave hidden-but-extractable "Prairie · {state}" text under the
  // parchment. drawHeaderBar happens inside the loop AFTER the cover.
  doc.addPage();
  let pageNumber = 1; // relative to plant pages, not the cover
  let cursor = margin + 20;

  // Reserve space for the next block; if it won't fit on the current page,
  // start a fresh page and reset the cursor below the header bar.
  const ensureSpace = (blockHeight) => {
    if (cursor + blockHeight > bottomLimit) {
      doc.addPage();
      pageNumber += 1;
      drawHeaderBar(pageNumber);
      cursor = margin + 20;
    }
  };

  // Sub-helpers that draw on the current cursor and advance it.
  const drawSectionLabel = (label) => {
    ensureSpace(16);
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(90);
    doc.text(decodeHtmlEntities(label.toUpperCase()), margin, cursor);
    cursor += 12;
  };
  const drawParagraph = (text, { size = 10, color = 40, indent = 0 } = {}) => {
    if (!text) return;
    const safe = decodeHtmlEntities(String(text));
    doc.setFont("helvetica", "normal"); doc.setFontSize(size); doc.setTextColor(color);
    const lines = doc.splitTextToSize(safe, pageW - 2 * margin - indent);
    // Line height ≈ font size * 1.25.
    const lh = size + 3;
    ensureSpace(lines.length * lh + 2);
    doc.text(lines, margin + indent, cursor);
    cursor += lines.length * lh + 2;
  };

  // Group items by life_cycle so the PDF reads like a field guide.
  // Order: annuals → biennials → perennials → unclassified (everything else).
  // Within each group we preserve the caller's original ordering so the
  // user's Gallery sort choice (date / name / etc.) is respected.
  const groupOf = (it) => {
    const lc = ((localizedIdentification(it, language) || {}).life_cycle || "").toLowerCase();
    return ["annual", "biennial", "perennial"].includes(lc) ? lc : "unclassified";
  };
  const GROUP_ORDER = ["annual", "biennial", "perennial", "unclassified"];
  items = [...items].sort((a, b) => {
    const ga = GROUP_ORDER.indexOf(groupOf(a));
    const gb = GROUP_ORDER.indexOf(groupOf(b));
    if (ga !== gb) return ga - gb;
    return 0;
  });

  const drawSectionCover = (groupKey, count) => {
    // Full-page section divider between life-cycle groups. Big centered
    // display heading + count line so the reader knows what's coming next
    // when they flip pages in a printed booklet.
    doc.setFillColor(247, 244, 236); // parchment
    doc.rect(0, 0, pageW, pageH, "F");
    doc.setTextColor(45, 74, 34); // leaf-deep
    doc.setFont("helvetica", "bold");
    doc.setFontSize(36);
    const title = t(`pdf.section.${groupKey}`);
    const titleW = doc.getTextWidth(title);
    doc.text(title, (pageW - titleW) / 2, pageH / 2 - 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(90);
    const sub = t("pdf.section.count", { count });
    const subW = doc.getTextWidth(sub);
    doc.text(sub, (pageW - subW) / 2, pageH / 2 + 14);
  };

  let currentGroup = null;
  // TOC entries collected as we render each plant. `actualPage` is the
  // jsPDF page number at the moment the plant title is drawn — captured
  // BEFORE we later insert TOC pages, so we adjust by +tocPageCount at the
  // end (see the TOC-injection block after the main loop).
  const tocEntries = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const id = localizedIdentification(it, language) || {};
    const itemGroup = groupOf(it);
    const groupChanged = itemGroup !== currentGroup;

    // Emit a full-page section divider whenever the group changes.
    // For the very first plant we reuse the already-added page (added just
    // after the cover) as the divider, then start the plant on a fresh page.
    if (groupChanged) {
      const groupCount = items.filter((x) => groupOf(x) === itemGroup).length;
      if (currentGroup !== null) {
        // Not the first section — start the divider on a new page.
        doc.addPage();
        pageNumber += 1;
      }
      drawSectionCover(itemGroup, groupCount);
      // The plant itself needs its own page after the divider.
      doc.addPage();
      pageNumber += 1;
      drawHeaderBar(pageNumber);
      cursor = margin + 20;
      currentGroup = itemGroup;
    } else if (i > 0) {
      // Same group, subsequent plant → fresh page for the plant (existing
      // "one plant per page" behavior).
      doc.addPage();
      pageNumber += 1;
      drawHeaderBar(pageNumber);
      cursor = margin + 20;
    }

    // --- Header row: title (left) + image (right) ---
    const imgW = 150, imgH = 112;
    // Capture this plant's page number for the TOC. `doc.getCurrentPageInfo`
    // returns the actual 1-indexed page; we'll bump by the # of inserted
    // TOC pages once we know that count.
    if (includeToc) {
      tocEntries.push({
        name: decodeHtmlEntities(id.common_name || t("pdf.unknown")),
        scientific: id.scientific_name ? decodeHtmlEntities(id.scientific_name) : null,
        group: itemGroup,
        renderedPage: doc.getCurrentPageInfo().pageNumber,
      });
    }
    // Reserve room for the header block (title + sci name + scanned line) OR
    // the image, whichever is taller — plus a little breathing room.
    const headerBlockHeight = Math.max(imgH, 62) + 12;
    ensureSpace(headerBlockHeight);

    const titleY = cursor + 4;
    doc.setTextColor(30);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(decodeHtmlEntities(id.common_name || t("pdf.unknown")), margin, titleY);

    let subY = titleY + 18;
    if (id.scientific_name) {
      doc.setFont("helvetica", "italic"); doc.setFontSize(11); doc.setTextColor(110);
      doc.text(decodeHtmlEntities(id.scientific_name), margin, subY);
      subY += 14;
    }
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(90);
    doc.text(
      decodeHtmlEntities(t("pdf.scanned", { date: new Date(it.created_at).toLocaleString() })),
      margin, subY
    );
    subY += 12;
    if (it.date_taken) {
      // Prefer a bare date if the value is a YYYY-MM-DD (no time component)
      // so we don't print a misleading "12:00:00 AM" back to the user.
      const dt = new Date(it.date_taken);
      const looksLikeDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(it.date_taken).trim());
      const dtStr = looksLikeDateOnly ? dt.toLocaleDateString() : dt.toLocaleString();
      doc.text(decodeHtmlEntities(t("pdf.photoTaken", { date: dtStr })), margin, subY);
      subY += 12;
    }
    if (it.location?.lat != null && it.location?.lng != null) {
      doc.text(
        decodeHtmlEntities(t("pdf.location", { lat: it.location.lat.toFixed(4), lng: it.location.lng.toFixed(4) })),
        margin, subY
      );
      subY += 12;
    }

    // Right-aligned image
    const imgX = pageW - margin - imgW;
    const imgY = cursor;
    const imgSrc = it.image_base64 || it.thumb_base64;
    if (imgSrc) drawImage(doc, imgSrc, imgX, imgY, imgW, imgH);
    else { doc.setDrawColor(220); doc.rect(imgX, imgY, imgW, imgH); }

    cursor += headerBlockHeight;

    // --- Attribute chips row (compact) ---
    const native = idNative(id);
    const endangered = idEndangered(id);
    const sunLabels = (id.sun_preference || []).map((s) => prettySunToken(s, t));
    const chips = [
      `${t("pdf.native")}: ${yesNo(native, t)}`,
      `${t("pdf.type")}: ${id.plant_type || t("pdf.unknown")}`,
      `${t("pdf.sun")}: ${sunLabels.join(", ") || t("pdf.unknown")}`,
      `${t("pdf.keystone")}: ${
        id.is_keystone
          ? (id.keystone_species_support?.length
              ? `${t("pdf.yes")} — ${id.keystone_species_support.join(", ")}`
              : t("pdf.yes"))
          : id.is_keystone === false ? t("pdf.no") : t("pdf.unknown")
      }`,
      `${t("pdf.endangered")}: ${yesNo(endangered, t)}`,
    ];
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(40);
    // Draw chips as pipe-separated wrapping lines to save vertical space.
    const chipText = chips.map(decodeHtmlEntities).join("   ·   ");
    const chipLines = doc.splitTextToSize(chipText, pageW - 2 * margin);
    ensureSpace(chipLines.length * 13 + 4);
    doc.text(chipLines, margin, cursor);
    cursor += chipLines.length * 13 + 6;

    // --- State status ---
    const stateNote = idStateNote(id);
    if (stateNote) {
      drawSectionLabel(t("pdf.stateStatus"));
      drawParagraph(stateNote);
    }

    // --- Companion plant ---
    if (id.companion_plant?.name) {
      drawSectionLabel(t("pdf.companion"));
      drawParagraph(id.companion_plant.name, { size: 11, color: 20 });
      if (id.companion_plant.reason) {
        drawParagraph(id.companion_plant.reason, { size: 10, color: 60 });
      }
    }

    // --- Common pests ---
    const pests = Array.isArray(id.common_pests) ? id.common_pests.filter((p) => p && (p.name || p.symptoms || p.natural_remedy)) : [];
    if (pests.length) {
      drawSectionLabel(t("pdf.pests"));
      for (const p of pests.slice(0, 3)) {
        if (p.name) drawParagraph(p.name, { size: 11, color: 20 });
        if (p.symptoms) drawParagraph(`${t("pdf.pestsSymptoms")} ${p.symptoms}`, { size: 10, color: 60, indent: 8 });
        if (p.natural_remedy) drawParagraph(`${t("pdf.pestsRemedy")} ${p.natural_remedy}`, { size: 10, color: 60, indent: 8 });
      }
    }

    // --- Medicinal / traditional uses ---
    const med = Array.isArray(id.medicinal_traditional_uses) ? id.medicinal_traditional_uses.filter(Boolean) : [];
    if (med.length) {
      drawSectionLabel(t("pdf.medicinal"));
      for (const u of med.slice(0, 4)) drawParagraph(`• ${u}`, { size: 10, color: 40, indent: 4 });
      drawParagraph(t("pdf.medicinalDisclaimer"), { size: 8, color: 120, indent: 4 });
    }

    // --- Cultural uses ---
    const cul = Array.isArray(id.cultural_uses) ? id.cultural_uses.filter(Boolean) : [];
    if (cul.length) {
      drawSectionLabel(t("pdf.cultural"));
      for (const u of cul.slice(0, 4)) drawParagraph(`• ${u}`, { size: 10, color: 40, indent: 4 });
    }

    // --- Notes ---
    if (id.notes) {
      drawSectionLabel(t("pdf.notes"));
      drawParagraph(id.notes, { size: 10, color: 40 });
    }
    // (No inter-plant separator — each plant already starts on its own page.)
  }

  // ------------------ Table of Contents (optional) ------------------
  // Render the TOC as its own set of pages inserted just after the cover.
  // We collected `renderedPage` for each entry as we drew the plant page,
  // referring to its jsPDF page number in the pre-TOC document. When we
  // insert N blank TOC pages between the cover and content, every plant
  // page shifts down by N — we compensate before drawing entry rows.
  if (includeToc && tocEntries.length > 0) {
    // Layout constants for the TOC pages.
    const tocLineH = 16;
    const tocTitleBlockH = 60; // header bar + title + spacing
    const availableH = bottomLimit - (margin + 20 + tocTitleBlockH);
    // Reserve room for a group heading (~28pt) before each group's entries.
    // Estimate conservatively so we don't miss inserting a page.
    const entriesPerPage = Math.max(6, Math.floor(availableH / tocLineH));
    // Estimated line count = entries + up to 4 group headings (annual /
    // biennial / perennial / unclassified). Overestimating pages is fine —
    // trailing pages we don't fill just stay blank, which is harmless. But
    // to be safe we compute the actual row count including headings.
    const groupOrder = ["annual", "biennial", "perennial", "unclassified"];
    const groupsPresent = groupOrder.filter((g) =>
      tocEntries.some((e) => e.group === g)
    );
    const totalRows = tocEntries.length + groupsPresent.length * 2; // heading + spacer per group
    const tocPageCount = Math.max(1, Math.ceil(totalRows / entriesPerPage));

    // Insert `tocPageCount` blank pages BEFORE page 2 (after the cover).
    // Note: jsPDF `insertPage(2)` inserts before position 2, so calling
    // it repeatedly at 2 keeps stacking new pages at position 2 while
    // pushing the previously-inserted ones down. All blank, order-agnostic.
    for (let i = 0; i < tocPageCount; i++) {
      doc.insertPage(2);
    }

    // Shift every entry's page number by the number of TOC pages we added.
    for (const e of tocEntries) e.renderedPage += tocPageCount;

    // Draw TOC rows across the inserted pages.
    let tocPageIdx = 0;
    let tocY = 0;
    let currentTocGroup = null;
    // Track loop index so `advanceTocRow` can safely bump only entries we
    // haven't drawn yet if we ever overflow the pre-allocation.
    let drawIdx = 0;
    const startTocPage = () => {
      doc.setPage(2 + tocPageIdx);
      // Header bar. Skip the "Contents" label on the first TOC page — the
      // big title below already announces the section, and repeating the
      // word right above it reads as redundant. Subsequent overflow pages
      // do get the running header so readers know they're still in the
      // Contents section.
      doc.setFillColor(45, 74, 34);
      doc.rect(0, 0, pageW, 24, "F");
      doc.setTextColor(255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      if (tocPageIdx > 0) {
        doc.text(decodeHtmlEntities(t("pdf.tocTitle")), margin, 16);
      }
      if (tocPageCount > 1) {
        doc.text(`${tocPageIdx + 1} / ${tocPageCount}`, pageW - margin, 16, { align: "right" });
      }
      tocY = margin + 24;
      if (tocPageIdx === 0) {
        // Big title only on the first TOC page.
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(30);
        doc.text(decodeHtmlEntities(t("pdf.tocTitle")), margin, tocY + 8);
        tocY += 28;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(110);
        doc.text(
          decodeHtmlEntities(t("pdf.tocSubtitle", { count: tocEntries.length })),
          margin, tocY + 4
        );
        tocY += 20;
      } else {
        tocY = margin + 30;
      }
    };
    startTocPage();

    const advanceTocRow = (rowH) => {
      if (tocY + rowH > bottomLimit) {
        tocPageIdx += 1;
        if (tocPageIdx >= tocPageCount) {
          // Pre-allocation under-counted. Insert a new blank TOC page and
          // ONLY shift entries we haven't already drawn (i >= drawIdx).
          // Already-drawn entries keep their pre-shift numbers because
          // they're already printed on prior TOC pages; bumping them here
          // would only affect future draws (there are none for them).
          doc.insertPage(2 + tocPageIdx);
          for (let i = drawIdx; i < tocEntries.length; i++) {
            tocEntries[i].renderedPage += 1;
          }
        }
        startTocPage();
      }
    };

    for (let i = 0; i < tocEntries.length; i++) {
      drawIdx = i;
      const entry = tocEntries[i];
      // Group heading whenever the group changes.
      if (entry.group !== currentTocGroup) {
        advanceTocRow(28);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(45, 74, 34);
        doc.text(decodeHtmlEntities(t(`pdf.section.${entry.group}`)), margin, tocY);
        tocY += 8;
        // Thin underline under the group heading.
        doc.setDrawColor(200);
        doc.setLineWidth(0.5);
        doc.line(margin, tocY, pageW - margin, tocY);
        tocY += 12;
        currentTocGroup = entry.group;
      }

      advanceTocRow(tocLineH);

      const pageStr = String(entry.renderedPage);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(40);
      const pageStrW = doc.getTextWidth(pageStr);

      // Name column — truncate if it would collide with the page column.
      const maxNameW = pageW - 2 * margin - pageStrW - 24;
      let name = entry.name;
      // Trim to visual width using splitTextToSize + first-line.
      if (doc.getTextWidth(name) > maxNameW) {
        const lines = doc.splitTextToSize(name, maxNameW);
        name = lines[0].replace(/\s+$/, "") + "…";
      }
      doc.text(name, margin, tocY);
      const nameW = doc.getTextWidth(name);

      // Dotted leader between name and page number.
      const leaderStart = margin + nameW + 4;
      const leaderEnd = pageW - margin - pageStrW - 4;
      if (leaderEnd > leaderStart + 8) {
        doc.setTextColor(180);
        doc.setFontSize(11);
        let x = leaderStart;
        const dot = ".";
        const dotStep = doc.getTextWidth(". ");
        while (x < leaderEnd) {
          doc.text(dot, x, tocY);
          x += dotStep;
        }
        doc.setTextColor(40);
      }

      // Page number right-aligned.
      doc.text(pageStr, pageW - margin, tocY, { align: "right" });

      tocY += tocLineH;
    }

    // Return to the last page in case downstream code relies on active page.
    doc.setPage(doc.internal.getNumberOfPages());
  }

  if (output === "blob") {
    return { blob: doc.output("blob"), filename };
  }
  doc.save(filename);
  return null;
}
